import React, { useRef, useState } from 'react';
import axios from 'axios';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FolderZipOutlinedIcon from '@mui/icons-material/FolderZipOutlined';
import CloseIcon from '@mui/icons-material/Close';
import PageLayout from '../layout/PageLayout';
import { notify, getErrorMessage } from '../feedback/notify';

const formatarTamanho = (bytes) => `${(bytes / 1024 / 1024).toFixed(2)} MB`;

/** Tela de importação de arquivo ZIP (guias de obrigações / demonstrações contábeis). */
export default function ImportarArquivosPage({ title, subtitle, breadcrumbs, endpoint, formField, uploadTitle, uploadDescription }) {
    const inputRef = useRef(null);
    const [filesSelected, setFilesSelected] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const selecionarArquivos = (lista) => {
        const arquivos = Array.from(lista || []);
        if (arquivos.length === 0) return false;
        const arquivosValidos = arquivos.filter((arquivo) => arquivo.name.toLowerCase().endsWith('.zip'));
        if (arquivosValidos.length !== arquivos.length) {
            notify.warning("Apenas arquivos ZIP são permitidos.");
            return false;
        }
        setFilesSelected(arquivosValidos);
        return true;
    };

    const handleProcessFiles = async () => {
        if (filesSelected.length === 0) {
            notify.warning("Selecione um arquivo para processar.");
            return;
        }
        if (loading) return;
        try {
            setLoading(true);
            const formData = new FormData();

            formData.append(formField, filesSelected[0]);

            await axios.post(`${process.env.REACT_APP_API_URL}/${endpoint}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: localStorage.getItem('token'),
                },
            });
            setFilesSelected([]);
            if (inputRef.current) inputRef.current.value = '';
            notify.success("Arquivos processados com sucesso!");
        } catch (error) {
            console.error("Erro ao enviar arquivos:", error);
            notify.error(getErrorMessage(error, "Não foi possível processar o arquivo. Verifique o conteúdo e tente novamente."));
        } finally {
            setLoading(false);
        }
    };

    const totalBytes = filesSelected.reduce((acc, file) => acc + file.size, 0);

    return (
        <PageLayout title={title} subtitle={subtitle} breadcrumbs={breadcrumbs}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.6fr 1fr' }, gap: 2, alignItems: 'start' }}>
                <Card sx={{ p: 2.5 }}>
                    <Box
                        onDragOver={(e) => { e.preventDefault(); if (!loading) setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setIsDragging(false);
                            if (!loading) selecionarArquivos(e.dataTransfer.files);
                        }}
                        sx={{
                            border: '1.5px dashed',
                            borderColor: isDragging ? 'secondary.main' : '#D0D5DD',
                            bgcolor: isDragging ? '#F6FEF9' : '#FCFCFD',
                            borderRadius: 2,
                            minHeight: 260,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            px: 3,
                            py: 4,
                            transition: 'border-color 150ms, background-color 150ms',
                        }}
                    >
                        <Box sx={{ width: 48, height: 48, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                            <CloudUploadOutlinedIcon sx={{ color: 'text.secondary' }} />
                        </Box>
                        <Typography variant="h5">{uploadTitle}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 2.5, maxWidth: 420 }}>
                            {uploadDescription} Arraste o arquivo para cá ou selecione no computador.
                        </Typography>
                        <Button component="label" variant="outlined" disabled={loading} startIcon={<FolderZipOutlinedIcon />}>
                            Selecionar arquivo .zip
                            <input
                                ref={inputRef}
                                hidden
                                type="file"
                                accept=".zip"
                                onChange={(event) => {
                                    if (!selecionarArquivos(event.target.files)) event.target.value = "";
                                }}
                            />
                        </Button>
                    </Box>

                    <Box sx={{ mt: 2.5 }}>
                        <Typography variant="subtitle2">Arquivo selecionado</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {filesSelected.length} arquivo(s) pronto(s) para importação
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {filesSelected.map((file, index) => (
                                <Box
                                    key={`${file.name}-${index}`}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, px: 1.5, py: 1 }}
                                >
                                    <FolderZipOutlinedIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }} noWrap>{file.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{formatarTamanho(file.size)}</Typography>
                                    </Box>
                                    {!loading && (
                                        <IconButton size="small" aria-label="Remover arquivo" onClick={() => { setFilesSelected([]); if (inputRef.current) inputRef.current.value = ''; }}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Card>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Card sx={{ p: 2.5 }}>
                        <Typography variant="h5">Resumo</Typography>
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Arquivos</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{filesSelected.length}</Typography>
                        </Box>
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Tamanho total</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatarTamanho(totalBytes)}</Typography>
                        </Box>
                        {loading && <LinearProgress color="secondary" sx={{ mt: 2.5, borderRadius: 1 }} />}
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            sx={{ mt: 2.5 }}
                            loading={loading}
                            loadingPosition="start"
                            disabled={filesSelected.length === 0}
                            onClick={handleProcessFiles}
                        >
                            {loading ? 'Processando...' : 'Processar arquivos'}
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                            {loading ? 'Aguarde enquanto os arquivos são processados.' : 'Finaliza a importação e registra no sistema.'}
                        </Typography>
                    </Card>

                    <Alert severity="info" variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                        Certifique-se de que os PDFs dentro do arquivo compactado não estão protegidos por senha, para evitar erros no processamento.
                    </Alert>
                </Box>
            </Box>
        </PageLayout>
    );
}
